// @ts-check
import { formatMoney } from './shopify-utils.js';

/**
 * @typedef {Object} ProductOption
 * @property {string} id - The unique identifier for the product option.
 * @property {number} price - The product price.
 * @property {number | null | undefined} originalPrice - The original product price.
 */

/**
 * @typedef {Object} ProductPrice
 * @property {number} price - The product price.
 * @property {number} originalPrice - The original product price.
 */

if (!customElements.get('total-price-with-options')) {
  class TotalPriceWithOptions extends HTMLElement {

    baseOriginalPrice = 0;
    basePrice = 0;

    /** @type {string | null} */
    productId = null;

    /** @type {ProductOption[]} */
    productOptions = [];
  
    constructor() {
      super();
    }

    connectedCallback() {
      this.productId = this.getAttribute('product-id') || '';

      const values = (this.getAttribute('values') || '0,0').split(',');
      this.baseOriginalPrice = values[0] ? parseFloat(values[0]) : 0;
      this.basePrice = values[1] ? Math.round(parseFloat(values[1])) : 0;
      // let basePriceElement = document.querySelector('.club-price');
      // this.basePrice =  parseInt(basePriceElement.dataset.price);

      this.renderTotalPrice();
      this.setupListeners();
    }

    setupListeners() {
      document.addEventListener('update-product-options', this.updateProductOptions.bind(this));
      document.addEventListener('update-product-price', this.updateProductPrice.bind(this));
    }

    updateProductPrice(/** @type {Event} */ event) {
      const option = /** @type {CustomEvent<ProductPrice>} */ (event);
      const { price, originalPrice } = option.detail;

      this.basePrice = Math.round(Number(price)) || 0;
      this.baseOriginalPrice = Math.round(Number(originalPrice)) || 0;

      this.renderTotalPrice();
    }

    updateProductOptions(/** @type {Event} */ event) {
      const option = /** @type {CustomEvent<ProductOption>} */ (event);
      const { id, price } = option.detail;
      
      const existingOptionIndex = this.productOptions.findIndex(opt => opt.id === id);
      if (existingOptionIndex !== -1) {
        this.productOptions = this.productOptions.filter(opt => opt.id !== id);
      } else {
        this.productOptions.push({ id, price: Number(price) || 0, originalPrice: Number(price) || 0 });
      }

      this.renderTotalPrice();
    }

    calculateTotalPrice() {
      let totalOriginalPrice = this.baseOriginalPrice;
      let totalPrice = this.basePrice;

      this.productOptions.forEach(opt => {
        totalOriginalPrice += opt.originalPrice || 0;
        totalPrice += opt.price || 0;
      });

      return {
        originalPrice: totalOriginalPrice,
        price: totalPrice
      };
    }

    renderTotalPrice() {
      const prices = this.calculateTotalPrice();
      const priceElement = this.querySelector('.total-price-with-options');

      if (priceElement) {
        priceElement.textContent = formatMoney(prices.price);
      }

      const xbpElement = document.querySelector('#_xbo');
      if (xbpElement && xbpElement instanceof HTMLInputElement) {
        xbpElement.value = String(prices.price);
      }
    }
  }

  customElements.define('total-price-with-options', TotalPriceWithOptions);
}

/**
 * @typedef {Object} ProductVariant
 * @property {string} id - Product ID
 * @property {boolean} available - If the product variant is available.
 * @property {boolean} selected - If the product variant is selected.
 * @property {number} price - The product variant price.
 * @property {number} compareAtPrice - The product variant compare at price.
 * @property {number} clubPrice - The product variant club price.
 */

/**
 * @typedef {Object} ProductData
 * @property {string} id - Product ID
 * @property {ProductVariant[]} variants - The product variants.
 * @property {ProductData[]} bundleProducts - The products in the bundle.
 */

if (!customElements.get('club-product')) {
  class ClubProduct extends HTMLElement {

    /** @type {ProductData | null} */
    productData = null;

    /** @type {string | null} */
    club = null;

    constructor() {
      super();
    }

    connectedCallback() {
      this.club = this.getAttribute('club');
    
      // @ts-ignore
      if (typeof productData !== 'undefined') {
        // @ts-ignore
        this.productData = productData;
      }

      this.updateCartClub();
    
      if (this.productData) {
        this.setupListeners();
      }
    }

    async updateCartClub() {
      const storedClub = localStorage.getItem('club');

      if (!this.club || this.club === storedClub) return;

      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ attributes: { _club: this.club } }),
          headers: {
            'Content-Type': 'application/json'
          }
        });
        localStorage.setItem('club', this.club);
      } catch (error) {
        console.error('Error updating cart club attribute:', error);
      }
    }
  
    setupListeners() {
      // Setup any event listeners if needed
      this.querySelectorAll('.bundle-variant-select').forEach(select => {
        select.addEventListener('change', this.handleBundleVariantChange.bind(this));
      });

      document.addEventListener('variant-change', this.handleVariantChange.bind(this));
    }

    handleVariantChange(/** @type {Event} */ event) {
      const customEvent = /** @type {CustomEvent} */ (event);
      const { variantId } = customEvent.detail;

      if (!this.productData) return;

      for (const variant of this.productData.variants) {
        variant.selected = variant.id === variantId;
      }

      this.updatePrices();
    }

    handleBundleVariantChange(/** @type {Event} */ event) {
      const select = /** @type {HTMLSelectElement} */ (event.target);
      const variantId = select.value;
      const productId = select.getAttribute('data-product-id');

      if (!productId || !this.productData?.bundleProducts?.length) return;

      const bundleProduct = this.productData.bundleProducts.find(p => p.id === productId);
      if (!bundleProduct) return;

      if (variantId) {
        for (const variant of bundleProduct.variants) {
          variant.selected = variant.id === variantId;
        }
      }
      
      // console.log('Bundle variant changed:', { productId, variantId });
      document.dispatchEvent(new CustomEvent(`${productId}-variant-change`, { detail: { variantId }, bubbles: true }));
      this.updatePrices();
    }

    updatePrices() {
      let totalPrice = 0;
      let totalClubPrice = 0;

      if (!this.productData) return;
    
      const clubPriceElements = this.querySelectorAll(`.club-price[data-product-id="${this.productData.id}"]`);
      const clubOriginalPriceElements = this.querySelectorAll(`.club-price-original[data-product-id="${this.productData.id}"]`);

      if (this.productData.bundleProducts) {
        this.productData.bundleProducts.forEach(bundleProduct => {
          const selectedVariant = bundleProduct.variants.find(variant => variant.selected);
          if (selectedVariant) {
            totalPrice += selectedVariant.price;
            totalClubPrice += selectedVariant.clubPrice;
          }
        });
      } else {
        const selectedVariant = this.productData.variants.find(variant => variant.selected);
        if (selectedVariant) {
          totalPrice = selectedVariant.price;
          totalClubPrice = selectedVariant.clubPrice;
        }
      }

      clubPriceElements.forEach(el => {
        el.textContent = formatMoney(Math.round(totalClubPrice));
      });

      clubOriginalPriceElements.forEach(el => {
        el.textContent = formatMoney(Math.round(totalPrice));
      });

      this.dispatchEvent(new CustomEvent('update-product-price', { 
        detail: { price: totalClubPrice, originalPrice: totalPrice },
        bubbles: true 
      }));
    }
  }
  
  customElements.define('club-product', ClubProduct);
}